import { z } from 'zod';
import { Platform } from './platform';

/**
 * 分析任务类型（ME-24 · 第八部分 AI 分析管线）。
 *
 * 取值与 shard 库 `analysis_tasks.task_type` / `aigc_records.task_type` 的 CHECK 约束保持一致，
 * 也与 ingest worker 入队时 crawl_purpose → task_type 的映射保持一致。新增类型务必三处同步。
 */
export const AnalysisTaskType = z.enum([
  // V0 启用
  'self_note_analysis',
  'competitor_insight',
  'skill_extraction',
  'topic_recommend',
  'daily_recommendation',
  'aigc_generation',
  'aigc_rewrite',
  'content_safety',
  // V2 启用（视频 / 跨平台）——当前仅占位，analyzer 暂不处理
  'video_transcription',
  'video_scene_detect',
  'video_ocr',
  'video_summarize',
  'cross_platform_compare',
]);
export type AnalysisTaskType = z.infer<typeof AnalysisTaskType>;

/**
 * analyzer Queue 消息契约——**权威定义**，生产者（ingest）与消费者（analyzer）共用。
 *
 * 历史上 analyzer 的内联 interface 与 ingest 实际发送的字段不一致（缺 tenant_id、task_type 枚举不符），
 * 导致消费者拿不到定位分片所需的 tenant_id。这里统一为单一来源，避免再次漂移。
 */
export const AnalyzerQueueMessage = z.object({
  task_id: z.string().min(1),
  task_type: AnalysisTaskType,
  tenant_id: z.string().min(1),
  note_id: z.string().min(1),
  platform: z.string().min(1),
  product_model_id: z.string().min(1).optional(),
  model_display_name: z.string().min(1).optional(),
  trigger_source: z.enum(['manual', 'automatic', 'internal']).optional(),
  // admin failed_jobs 重放时由 API 注入；入库 payload 不带该字段，避免污染原始任务契约。
  failed_job_id: z.number().int().positive().optional(),
});
export type AnalyzerQueueMessage = z.infer<typeof AnalyzerQueueMessage>;

export const DAILY_RECOMMENDATION_COUNT = 3;

export const DailyRecommendationEvidence = z.object({
  note_id: z.string().trim().min(1).max(128),
  title: z.string().trim().min(1).max(120),
  reason: z.string().trim().min(1).max(240),
});

export const DailyRecommendationFitAssessment = z.object({
  summary: z.string().trim().min(1).max(240),
  basis: z
    .array(z.enum(['account_profile', 'account_diagnosis', 'competitor_evidence']))
    .min(1)
    .max(3),
  confidence: z.enum(['low', 'medium', 'high']),
});

export const DailyRecommendationModelItemOutput = z.object({
  title: z.string().trim().min(1).max(120),
  topic: z.string().trim().min(1).max(200),
  angle: z.string().trim().min(1).max(240),
  reason: z.string().trim().min(1).max(360),
  fit_assessment: DailyRecommendationFitAssessment,
  generation_brief: z.string().trim().min(1).max(400),
  evidence: z.array(DailyRecommendationEvidence).min(1).max(8),
});

export const DailyRecommendationItem = DailyRecommendationModelItemOutput.extend({
  id: z.string().trim().min(1).max(128),
});

export const DailyRecommendationGroup = z.object({
  id: z.string().min(1),
  status: z.enum(['pending', 'ready', 'failed']),
  confidence: z.enum(['low_data', 'standard']),
  items: z.array(DailyRecommendationItem).max(DAILY_RECOMMENDATION_COUNT),
  generated_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  can_refresh: z.boolean(),
  next_auto_refresh_at: z.string().nullable(),
});
export type DailyRecommendationGroup = z.infer<typeof DailyRecommendationGroup>;

export const DailyRecommendationResponse = z.object({
  group: DailyRecommendationGroup.nullable(),
  data_sufficiency: z.object({
    sufficient: z.boolean(),
    competitor_note_count: z.number().int().nonnegative(),
    marked_note_count: z.number().int().nonnegative(),
    missing: z.array(z.enum(['self_profile', 'competitor_notes', 'engagement_or_mark'])),
  }),
});
export type DailyRecommendationResponse = z.infer<typeof DailyRecommendationResponse>;

export const DailyRecommendationFeedbackRequest = z.object({
  platform: Platform,
  item_id: z.string().min(1),
  feedback: z.enum(['valuable', 'not_for_me']),
});
export type DailyRecommendationFeedbackRequest = z.infer<typeof DailyRecommendationFeedbackRequest>;

/**
 * 模型原始输出的宽松校验（第八部分要求：Zod 校验模型输出，不合格走重试）。
 *
 * 故意保持「形状严格、数值宽松」：
 * - summary / tags 必须是字符串与字符串数组，缺失或类型错误 → 校验失败 → 重试；
 * - score / originality 用 coerce 容忍模型把数字写成字符串（"85"），但仍要求落在 0~100；
 * - tags 目标 3~5，但校验上限放宽到 20：模型多给几个标签不该触发重试，多余的由
 *   {@link normalizeNoteInsight} 去重并截断到 5（落库稳定 ≤5），而非判失败重算。
 * 归一化（去重、截断、取整）交给 {@link normalizeNoteInsight}，避免把「能修的」误判为「该重试的」。
 */
export const NoteInsightModelOutput = z.object({
  summary: z.string().trim().min(1).max(512),
  tags: z.array(z.string().trim().min(1).max(32)).min(1).max(20), // 宽松上限，归一化截断到 5
  score: z.coerce.number().min(0).max(100),
  originality: z.coerce.number().min(0).max(100),
});
export type NoteInsightModelOutput = z.infer<typeof NoteInsightModelOutput>;

export const SelfNoteAnalysisEvidenceSource = z.enum(['body', 'metrics', 'metadata']);
export type SelfNoteAnalysisEvidenceSource = z.infer<typeof SelfNoteAnalysisEvidenceSource>;

export const SelfNoteAnalysisDimensionCode = z.enum([
  'topic_selection',
  'hook',
  'structure',
  'clarity',
  'evidence',
  'engagement',
  'call_to_action',
  'originality',
  'metadata_quality',
  'other',
]);
export type SelfNoteAnalysisDimensionCode = z.infer<typeof SelfNoteAnalysisDimensionCode>;

export const SelfNoteAnalysisFindingModelOutput = z.object({
  dimension_code: SelfNoteAnalysisDimensionCode,
  dimension_label: z.string().trim().min(1).max(80),
  evidence: z.string().trim().min(1).max(240),
  impact: z.string().trim().min(1).max(240),
  evidence_source: SelfNoteAnalysisEvidenceSource,
});
export type SelfNoteAnalysisFindingModelOutput = z.infer<typeof SelfNoteAnalysisFindingModelOutput>;

export const SelfNoteAnalysisImprovementModelOutput = z.object({
  dimension_code: SelfNoteAnalysisDimensionCode,
  action: z.string().trim().min(1).max(240),
  reason: z.string().trim().min(1).max(240),
  priority: z.enum(['high', 'medium', 'low']),
  evidence_source: SelfNoteAnalysisEvidenceSource,
});
export type SelfNoteAnalysisImprovementModelOutput = z.infer<
  typeof SelfNoteAnalysisImprovementModelOutput
>;

export const SelfNoteAnalysisContentFeatureCode = z.enum([
  'topic',
  'hook_style',
  'structure',
  'cta_style',
]);
export type SelfNoteAnalysisContentFeatureCode = z.infer<typeof SelfNoteAnalysisContentFeatureCode>;

export const SelfNoteAnalysisContentFeaturesModelOutput = z.object({
  feature_code: SelfNoteAnalysisContentFeatureCode,
  value: z.string().trim().min(1).max(120).nullable(),
  evidence_source: SelfNoteAnalysisEvidenceSource,
});
export type SelfNoteAnalysisContentFeaturesModelOutput = z.infer<
  typeof SelfNoteAnalysisContentFeaturesModelOutput
>;

/** self_note_analysis 的独立模型合同；不扩大竞品、选题或普通笔记分析输出。 */
export const SelfNoteAnalysisInsightModelOutput = NoteInsightModelOutput.extend({
  strengths: z.array(SelfNoteAnalysisFindingModelOutput).max(12),
  issues: z.array(SelfNoteAnalysisFindingModelOutput).max(12),
  improvements: z.array(SelfNoteAnalysisImprovementModelOutput).max(12),
  content_features: z.array(SelfNoteAnalysisContentFeaturesModelOutput).max(4),
  data_quality: z.object({
    body_available: z.boolean(),
    metrics_available: z.boolean(),
    metadata_available: z.boolean(),
    confidence: z.enum(['low', 'medium', 'high']),
  }),
  limitations: z.array(z.string().trim().min(1).max(240)).max(8),
});
export type SelfNoteAnalysisInsightModelOutput = z.infer<typeof SelfNoteAnalysisInsightModelOutput>;

export const CompetitorHotspotPatternsModelOutput = z.object({
  structure: z.array(z.string().trim().min(1).max(120)).min(1).max(12),
  hook_sentences: z.array(z.string().trim().min(1).max(120)).min(1).max(12),
  publish_timing: z.array(z.string().trim().min(1).max(80)).min(1).max(12),
  tags: z.array(z.string().trim().min(1).max(32)).min(1).max(20),
});
export type CompetitorHotspotPatternsModelOutput = z.infer<
  typeof CompetitorHotspotPatternsModelOutput
>;

export const CompetitorEngagementAttributionModelOutput = z.object({
  overall: z.string().trim().min(1).max(512),
  likes: z.array(z.string().trim().min(1).max(120)).min(1).max(10),
  collects: z.array(z.string().trim().min(1).max(120)).min(1).max(10),
  comments: z.array(z.string().trim().min(1).max(120)).min(1).max(10),
  shares: z.array(z.string().trim().min(1).max(120)).min(1).max(10),
});
export type CompetitorEngagementAttributionModelOutput = z.infer<
  typeof CompetitorEngagementAttributionModelOutput
>;

export const CompetitorInsightModelOutput = NoteInsightModelOutput.extend({
  hotspot_patterns: CompetitorHotspotPatternsModelOutput,
  engagement_attribution: CompetitorEngagementAttributionModelOutput,
});
export type CompetitorInsightModelOutput = z.infer<typeof CompetitorInsightModelOutput>;

// topic_recommend V0 固定产物规模：提示词与归一化共用，避免输出预算漂移。
export const TOPIC_RECOMMENDATION_COUNT = 3;
export const TOPIC_COMPETITOR_REF_MAX = 2;
export const TOPIC_SKILL_HINT_MAX = 2;
export const TOPIC_GENERATION_BRIEF_MAX = 400;

export const TopicCompetitorReferenceModelOutput = z.object({
  note_id: z.string().trim().min(1).max(128),
  title: z.string().trim().min(1).max(120),
  reason: z.string().trim().min(1).max(240),
});
export type TopicCompetitorReferenceModelOutput = z.infer<
  typeof TopicCompetitorReferenceModelOutput
>;

export const TopicSkillHintModelOutput = z.object({
  skill_id: z.string().trim().min(1).max(128).nullable().optional(),
  skill_name: z.string().trim().min(1).max(80),
  layer: z.coerce.number().int().min(1).max(5),
  prompt_hint: z.string().trim().min(1).max(320),
});
export type TopicSkillHintModelOutput = z.infer<typeof TopicSkillHintModelOutput>;

export const TopicOpportunityModelOutput = z.object({
  title: z.string().trim().min(1).max(120),
  angle: z.string().trim().min(1).max(240),
  why: z.string().trim().min(1).max(360),
  account_gap: z.string().trim().min(1).max(240),
  priority_score: z.coerce.number().min(0).max(100),
  competitor_refs: z.array(TopicCompetitorReferenceModelOutput).min(1).max(8), // 宽松上限，归一化截断到 2
  skill_hints: z.array(TopicSkillHintModelOutput).min(1).max(5), // 宽松上限，归一化截断到 2
  generation_topic: z.string().trim().min(1).max(200),
  generation_brief: z.string().trim().min(1).max(TOPIC_GENERATION_BRIEF_MAX),
});
export type TopicOpportunityModelOutput = z.infer<typeof TopicOpportunityModelOutput>;

export const TopicRecommendationModelOutput = NoteInsightModelOutput.extend({
  recommendations: z.array(TopicOpportunityModelOutput).min(1).max(12), // 宽松上限，归一化截断到 3
});
export type TopicRecommendationModelOutput = z.infer<typeof TopicRecommendationModelOutput>;

/** 归一化后的最小洞察集：一句话摘要 + 3~5 标签 + 内容质量分 + 原创度雏形。 */
export interface NoteInsight {
  summary: string;
  tags: string[];
  /** 内容质量分，整数 0~100。 */
  score: number;
  /** 原创度雏形，整数 0~100。 */
  originality: number;
}

export interface SelfNoteAnalysisFinding {
  dimension_code: SelfNoteAnalysisDimensionCode;
  dimension: string;
  evidence: string;
  impact: string;
  evidence_source: SelfNoteAnalysisEvidenceSource;
}

export interface SelfNoteAnalysisImprovement {
  dimension_code: SelfNoteAnalysisDimensionCode;
  action: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  evidence_source: SelfNoteAnalysisEvidenceSource;
}

export interface SelfNoteAnalysisContentFeatures {
  topic: string | null;
  hook_style: string | null;
  structure: string | null;
  cta_style: string | null;
}

export interface SelfNoteAnalysisInsight extends NoteInsight {
  strengths: SelfNoteAnalysisFinding[];
  issues: SelfNoteAnalysisFinding[];
  improvements: SelfNoteAnalysisImprovement[];
  content_features: SelfNoteAnalysisContentFeatures;
  data_quality: {
    body_available: boolean;
    metrics_available: boolean;
    metadata_available: boolean;
    confidence: 'low' | 'medium' | 'high';
  };
  limitations: string[];
}

export interface CompetitorHotspotPatterns {
  /** 爆款正文结构规律。 */
  structure: string[];
  /** 常见钩子句式，可直接给运营拆解。 */
  hook_sentences: string[];
  /** 发布时间段/节奏规律。 */
  publish_timing: string[];
  /** 高频标签/话题。 */
  tags: string[];
}

export interface CompetitorEngagementAttribution {
  /** 对公开互动数据的整体归因。 */
  overall: string;
  likes: string[];
  collects: string[];
  comments: string[];
  shares: string[];
}

export interface CompetitorInsight extends NoteInsight {
  hotspot_patterns: CompetitorHotspotPatterns;
  engagement_attribution: CompetitorEngagementAttribution;
}

export const CompetitorInsightRequestResponse = z.object({
  task_id: z.string().min(1),
  status: z.enum(['queued', 'running', 'succeeded']),
});
export type CompetitorInsightRequestResponse = z.infer<typeof CompetitorInsightRequestResponse>;

export const CompetitorInsightRequest = z.object({
  model_id: z.string().trim().min(1).max(128),
});
export type CompetitorInsightRequest = z.infer<typeof CompetitorInsightRequest>;

export const CompetitorInsightDetailResponse = CompetitorInsightModelOutput.extend({
  task_id: z.string().min(1),
  model: z.string().nullable(),
  analyzed_at: z.string().nullable(),
  original: z.object({
    title: z.string().nullable(),
    author: z.string().nullable(),
    body: z.string().nullable(),
    tags: z.array(z.string()),
    engagement: z.record(z.string(), z.number()),
    source_url: z.string().nullable(),
    published_at: z.string().nullable(),
  }),
});
export type CompetitorInsightDetailResponse = z.infer<typeof CompetitorInsightDetailResponse>;

export interface TopicCompetitorReference {
  note_id: string;
  title: string;
  reason: string;
}

export interface TopicSkillHint {
  /** C4 生成可直接使用；模型只能从候选 Skill 中选择，未知时为 null。 */
  skill_id: string | null;
  skill_name: string;
  layer: 1 | 2 | 3 | 4 | 5;
  prompt_hint: string;
}

export interface TopicOpportunity {
  /** 选题标题方向，直接作为生成 topic 的主候选。 */
  title: string;
  /** 内容切入角度。 */
  angle: string;
  /** 为什么值得做：竞品规律 + 自号缺口的交集。 */
  why: string;
  /** 命中的账号诊断缺口。 */
  account_gap: string;
  /** 机会优先级，整数 0~100。 */
  priority_score: number;
  /** 可追溯的竞品爆款参考。 */
  competitor_refs: TopicCompetitorReference[];
  /** 为 C4 一键生成准备的 Skill 提示。 */
  skill_hints: TopicSkillHint[];
  /** C4 生成接口的 topic 预填值。 */
  generation_topic: string;
  /** C4 生成接口的 brief 预填值。 */
  generation_brief: string;
}

export interface TopicRecommendation extends NoteInsight {
  recommendations: TopicOpportunity[];
}

export type AnalyzerInsight =
  | NoteInsight
  | SelfNoteAnalysisInsight
  | CompetitorInsight
  | TopicRecommendation;

export const CompetitorEngagementTotals = z.object({
  likes: z.number().int().nonnegative(),
  collects: z.number().int().nonnegative(),
  comments: z.number().int().nonnegative(),
  shares: z.number().int().nonnegative(),
  plays: z.number().int().nonnegative(),
});
export type CompetitorEngagementTotals = z.infer<typeof CompetitorEngagementTotals>;

const CompetitorTopNote = z.object({
  note_id: z.string(),
  title: z.string().nullable(),
  summary: z.string().nullable(),
  engagement_total: z.number().int().nonnegative(),
});

export const CompetitorCreatorAnalysisSchema = z.object({
  version: z.literal(1),
  task_type: z.literal('competitor_insight'),
  updated_at: z.string(),
  source_note_id: z.string(),
  creator: z.object({
    creator_id: z.string(),
    external_id: z.string(),
    // 参与聚合的最近笔记数（当前 creator 最多 50 篇），不是平台侧真实全量。
    note_count: z.number().int().nonnegative(),
    analyzed_note_count: z.number().int().nonnegative(),
    top_tags: z.array(z.string()),
    publish_windows: z.array(z.string()),
    engagement_totals: CompetitorEngagementTotals,
    top_notes: z.array(CompetitorTopNote),
    // 当前 V0 取自触发本次写回的单篇 insight；数值列表字段才是多篇聚合。
    patterns: CompetitorHotspotPatternsModelOutput,
    engagement_attribution: CompetitorEngagementAttributionModelOutput,
  }),
  track: z
    .object({
      account_id: z.string(),
      // 参与赛道聚合的最近笔记数（当前 track 最多 200 篇），不是平台侧真实全量。
      note_count: z.number().int().nonnegative(),
      creator_count: z.number().int().nonnegative(),
      top_tags: z.array(z.string()),
      publish_windows: z.array(z.string()),
      engagement_totals: CompetitorEngagementTotals,
    })
    .nullable(),
});
export type CompetitorCreatorAnalysis = z.infer<typeof CompetitorCreatorAnalysisSchema>;

const SUMMARY_MAX = 480; // 留余量，避开 notes.analysis_summary 的 length<=512 约束
const TAGS_MAX = 5;

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

/** 把校验通过的模型输出收敛为可落库的洞察集（去重标签、限 5 个、摘要截断、分数取整夹紧）。 */
export function normalizeNoteInsight(raw: NoteInsightModelOutput): NoteInsight {
  const tags = [...new Set(raw.tags.map((tag) => tag.trim()).filter(Boolean))].slice(0, TAGS_MAX);
  return {
    summary: raw.summary.trim().slice(0, SUMMARY_MAX),
    tags,
    score: clampScore(raw.score),
    originality: clampScore(raw.originality),
  };
}

const SELF_NOTE_ANALYSIS_FINDING_MAX = 5;

export function normalizeSelfNoteAnalysisInsight(
  raw: SelfNoteAnalysisInsightModelOutput,
  options: { bodyAvailable: boolean; metricsAvailable?: boolean; metadataAvailable?: boolean },
): SelfNoteAnalysisInsight {
  const base = normalizeNoteInsight(raw);
  const canUse = (source: SelfNoteAnalysisEvidenceSource) =>
    options.bodyAvailable || source !== 'body';
  const normalizeFinding = (
    finding: SelfNoteAnalysisFindingModelOutput,
  ): SelfNoteAnalysisFinding => ({
    dimension_code: finding.dimension_code,
    dimension: finding.dimension_label.trim().slice(0, 80),
    evidence: finding.evidence.trim().slice(0, 240),
    impact: finding.impact.trim().slice(0, 240),
    evidence_source: finding.evidence_source,
  });
  const contentFeatures: SelfNoteAnalysisContentFeatures = {
    topic: null,
    hook_style: null,
    structure: null,
    cta_style: null,
  };
  for (const feature of raw.content_features) {
    if (feature.evidence_source === 'body' && !options.bodyAvailable) continue;
    contentFeatures[feature.feature_code] = feature.value?.trim().slice(0, 120) || null;
  }
  return {
    ...base,
    strengths: raw.strengths
      .filter((finding) => canUse(finding.evidence_source))
      .slice(0, SELF_NOTE_ANALYSIS_FINDING_MAX)
      .map(normalizeFinding),
    issues: raw.issues
      .filter((finding) => canUse(finding.evidence_source))
      .slice(0, SELF_NOTE_ANALYSIS_FINDING_MAX)
      .map(normalizeFinding),
    improvements: raw.improvements
      .filter((item) => canUse(item.evidence_source))
      .slice(0, SELF_NOTE_ANALYSIS_FINDING_MAX)
      .map((item) => ({
        dimension_code: item.dimension_code,
        action: item.action.trim().slice(0, 240),
        reason: item.reason.trim().slice(0, 240),
        priority: item.priority,
        evidence_source: item.evidence_source,
      })),
    content_features: contentFeatures,
    data_quality: {
      body_available: options.bodyAvailable,
      metrics_available: options.metricsAvailable ?? raw.data_quality.metrics_available,
      metadata_available: options.metadataAvailable ?? raw.data_quality.metadata_available,
      confidence: raw.data_quality.confidence,
    },
    limitations: normalizeStringList(raw.limitations, 8),
  };
}

function normalizeStringList(values: string[], max: number): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].slice(0, max);
}

export function normalizeCompetitorInsight(raw: CompetitorInsightModelOutput): CompetitorInsight {
  const base = normalizeNoteInsight(raw);
  return {
    ...base,
    hotspot_patterns: {
      structure: normalizeStringList(raw.hotspot_patterns.structure, 8),
      hook_sentences: normalizeStringList(raw.hotspot_patterns.hook_sentences, 8),
      publish_timing: normalizeStringList(raw.hotspot_patterns.publish_timing, 8),
      tags: normalizeStringList(raw.hotspot_patterns.tags, TAGS_MAX),
    },
    engagement_attribution: {
      overall: raw.engagement_attribution.overall.trim().slice(0, SUMMARY_MAX),
      likes: normalizeStringList(raw.engagement_attribution.likes, 6),
      collects: normalizeStringList(raw.engagement_attribution.collects, 6),
      comments: normalizeStringList(raw.engagement_attribution.comments, 6),
      shares: normalizeStringList(raw.engagement_attribution.shares, 6),
    },
  };
}

export function normalizeTopicRecommendation(
  raw: TopicRecommendationModelOutput,
): TopicRecommendation {
  const base = normalizeNoteInsight(raw);
  return {
    ...base,
    recommendations: raw.recommendations.slice(0, TOPIC_RECOMMENDATION_COUNT).map((item) => ({
      title: item.title.trim().slice(0, 120),
      angle: item.angle.trim().slice(0, 240),
      why: item.why.trim().slice(0, 360),
      account_gap: item.account_gap.trim().slice(0, 240),
      priority_score: clampScore(item.priority_score),
      competitor_refs: item.competitor_refs.slice(0, TOPIC_COMPETITOR_REF_MAX).map((ref) => ({
        note_id: ref.note_id.trim().slice(0, 128),
        title: ref.title.trim().slice(0, 120),
        reason: ref.reason.trim().slice(0, 240),
      })),
      skill_hints: item.skill_hints.slice(0, TOPIC_SKILL_HINT_MAX).map((hint) => ({
        skill_id: hint.skill_id?.trim().slice(0, 128) || null,
        skill_name: hint.skill_name.trim().slice(0, 80),
        layer: Math.max(1, Math.min(5, Math.round(hint.layer))) as 1 | 2 | 3 | 4 | 5,
        prompt_hint: hint.prompt_hint.trim().slice(0, 320),
      })),
      generation_topic: item.generation_topic.trim().slice(0, 200),
      generation_brief: item.generation_brief.trim().slice(0, TOPIC_GENERATION_BRIEF_MAX),
    })),
  };
}

/** 笔记分析状态，与 shard 库 `notes.analysis_status` / `analysis_tasks.status` 子集一致。 */
export const NoteAnalysisStatus = z.enum(['pending', 'running', 'succeeded', 'failed', 'skipped']);
export type NoteAnalysisStatus = z.infer<typeof NoteAnalysisStatus>;

/**
 * 读路径暴露给前端的分析视图（denormalize 自 notes 行，零 JOIN）。
 * 未分析或被跳过时多数字段为 null，前端按 status 决定展示「分析中 / 已完成 / 暂不支持」。
 */
export interface NoteAnalysis {
  status: NoteAnalysisStatus;
  summary: string | null;
  tags: string[];
  score: number | null;
  originality: number | null;
  model: string | null;
  analyzed_at: string | null;
  /** C3 选题推荐明细；非 topic_recommend 笔记为空数组。 */
  recommendations: TopicOpportunity[];
}
